import React, { useState, useEffect, useCallback } from 'react';
import { api } from '@/lib/api';
import { logger } from '@/lib/logger';
import { Spinner, EnvelopeSimple, Sparkle, Trash, Copy, Check, User } from '@phosphor-icons/react';

export const RequestLettersSection = ({ caseId }) => {
  const [letters, setLetters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [copiedId, setCopiedId] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/cases/${caseId}/request-letters`);
      setLetters(data.letters || []);
    } catch (err) {
      logger.error('failed to load request letters:', err);
    } finally {
      setLoading(false);
    }
  }, [caseId]);

  useEffect(() => { load(); }, [load]);

  const generate = async () => {
    setGenerating(true);
    try {
      const { data } = await api.post(`/cases/${caseId}/request-letters/generate`, {});
      setLetters(data.letters || []);
    } catch (err) {
      logger.error('failed to generate request letters:', err);
      alert(err?.response?.data?.detail || 'Failed to draft letters. Add or generate some open questions first.');
    } finally {
      setGenerating(false);
    }
  };

  const remove = async (lid) => {
    setLetters(prev => prev.filter(l => l._id !== lid));
    try {
      await api.delete(`/cases/${caseId}/request-letters/${lid}`);
    } catch (err) {
      logger.error('failed to delete letter:', err);
      load();
    }
  };

  const copy = async (letter) => {
    try {
      await navigator.clipboard.writeText(`Subject: ${letter.subject}\n\n${letter.body}`);
      setCopiedId(letter._id);
      setTimeout(() => setCopiedId(null), 2000);
    } catch (err) {
      logger.error('clipboard copy failed:', err);
    }
  };

  if (loading) return null;

  return (
    <div className="border-t border-[#E5E7EB] pt-6 mt-8" data-testid="request-letters-section">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h4 className="font-outfit text-base font-semibold text-[#09090B] flex items-center gap-2">
            <EnvelopeSimple size={18} className="text-[#002FA7]" weight="fill" /> Information Request Letters
          </h4>
          <p className="text-sm text-[#6B7280] font-ibm mt-0.5 max-w-2xl">
            Turn your open questions into professional, ready-to-send request letters — grouped by recipient, so you ask each person everything at once.
          </p>
        </div>
        <button onClick={generate} disabled={generating} data-testid="letters-generate-button"
          className="flex items-center gap-1.5 px-4 py-2 bg-[#002FA7] text-white font-ibm font-semibold rounded-md text-sm hover:bg-[#00237D] disabled:opacity-50 transition-all shrink-0">
          {generating ? <><Spinner size={14} className="animate-spin" /> Drafting…</> : <><Sparkle size={14} weight="fill" /> Draft request letters</>}
        </button>
      </div>

      {letters.length === 0 ? (
        <p className="text-sm text-[#94A3B8] font-ibm italic mt-4" data-testid="letters-empty">
          No letters drafted yet. Drafting uses your open (and asked) questions and groups them by recipient.
        </p>
      ) : (
        <div className="space-y-4 mt-4">
          {letters.map(letter => (
            <div key={letter._id} className="bg-white border border-[#E5E7EB] rounded-lg p-4" data-testid={`letter-${letter._id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-xs font-ibm text-[#6B7280] flex items-center gap-1"><User size={11} /> To: <span className="font-semibold text-[#09090B]">{letter.recipient || 'Unspecified recipient'}</span></p>
                  <p className="text-sm font-ibm font-semibold text-[#09090B] mt-1" data-testid={`letter-subject-${letter._id}`}>{letter.subject}</p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <button onClick={() => copy(letter)} data-testid={`letter-copy-${letter._id}`}
                    className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-ibm font-semibold rounded-md border border-[#E5E7EB] text-[#6B7280] hover:text-[#002FA7] hover:border-[#002FA7]/40 transition-all">
                    {copiedId === letter._id ? <><Check size={12} className="text-green-600" /> Copied</> : <><Copy size={12} /> Copy</>}
                  </button>
                  <button onClick={() => remove(letter._id)} data-testid={`letter-delete-${letter._id}`}
                    className="flex items-center gap-1 px-2 py-1.5 text-xs font-ibm text-[#6B7280] hover:text-red-600 transition-all">
                    <Trash size={12} />
                  </button>
                </div>
              </div>
              <pre className="text-sm font-ibm text-[#374151] whitespace-pre-wrap leading-relaxed mt-3 bg-[#F9FAFB] border border-[#F3F4F6] rounded-md p-3" data-testid={`letter-body-${letter._id}`}>{letter.body}</pre>
              {(letter.question_texts || []).length > 0 && (
                <p className="text-[11px] text-[#94A3B8] font-ibm mt-2">Covers {letter.question_texts.length} open question{letter.question_texts.length > 1 ? 's' : ''}</p>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
